import { Monitor, Cloud, Brain, Heart, Star, BarChart3, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useNavigate } from "react-router-dom";

const SOLUTIONS = [
  {
    icon: Monitor,
    title: "Programmatic DOOH",
    desc: "Advanced programmatic buying for maximum reach & impact",
    href: "/partners",
    tile: "from-[hsl(217_91%_60%)] to-[hsl(213_94%_68%)]",
  },
  {
    icon: Cloud,
    title: "CMS Cloud System",
    desc: "Manage your content across all faces in real-time",
    href: "https://cms.fusion-media.sa",
    external: true,
    tile: "from-[hsl(195_85%_55%)] to-[hsl(190_90%_60%)]",
  },
  {
    icon: Brain,
    title: "AI Campaign Creator",
    desc: "Create, launch and optimize campaigns using AI power",
    href: "/ai-campaign-creator",
    tile: "from-[hsl(262_83%_65%)] to-[hsl(280_85%_70%)]",
  },
  {
    icon: Heart,
    title: "Influencer Advertising",
    desc: "Connect with top influencers & amplify your brand",
    href: "/influencers",
    tile: "from-[hsl(340_85%_62%)] to-[hsl(350_90%_68%)]",
  },
  {
    icon: Star,
    title: "Event Sponsorships",
    desc: "Sponsor events & maximize your brand visibility",
    href: "/events-sponsors",
    tile: "from-[hsl(32_95%_60%)] to-[hsl(20_95%_62%)]",
  },
  {
    icon: BarChart3,
    title: "Real-Time Analytics",
    desc: "Track performance with advanced real-time analytics",
    href: "/client-dashboard",
    tile: "from-[hsl(158_64%_52%)] to-[hsl(168_70%_52%)]",
  },
];

export const SolutionsGrid = () => {
  const { ref } = useRevealOnScroll<HTMLDivElement>();
  const navigate = useNavigate();

  return (
    <section ref={ref} className="reveal-on-scroll">
      <div className="max-w-2xl mb-10">
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
          <span className="bg-gradient-to-r from-primary to-primary-light bg-clip-text text-transparent">
            All-in-one
          </span>{" "}
          <span className="text-foreground">Advertising Solutions</span>
        </h2>
        <p className="text-base text-muted-foreground">
          Everything you need to plan, launch, and optimize advertising at scale.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {SOLUTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.title}
              onClick={() => {
                if ((s as any).external) {
                  window.open(s.href, "_blank", "noopener,noreferrer");
                } else {
                  navigate(s.href);
                }
              }}
              className="text-start group bg-card border border-border/60 rounded-2xl p-5 shadow-card hover:-translate-y-1 hover:shadow-stat transition-all duration-300 flex flex-col"
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.tile} flex items-center justify-center mb-4 shadow-md`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-sm md:text-base font-bold text-foreground mb-1.5 leading-tight">{s.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed mb-4 flex-1">{s.desc}</p>
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <ArrowRight className="w-4 h-4" />
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
};
